import type { HTMLAttributes, ReactNode } from "react";
import styles from "./RadioGroup.module.css";
import { cx } from "../cx";

export type RadioOption<V extends string = string> = {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  /** 프로필 행(ProfileRadioGroup 87:3211)용 아바타 40 슬롯. */
  leading?: ReactNode;
  disabled?: boolean;
};

export type RadioGroupProps<V extends string = string> = Omit<HTMLAttributes<HTMLDivElement>, "onChange"> & {
  options: readonly RadioOption<V>[];
  value: V | null;
  onChange: (value: V) => void;
  "aria-label"?: string;
};

/**
 * SEED RadioGroup (Figma 87:3123). 라디오 22, 선택 brand. 세로 목록.
 * 기본형 = 라디오 앞 + 행 구분선, 좌우 여백 없음(컨테이너가 준다). leading(아바타)이 있으면 ProfileRadioGroup(87:3211) = 라디오 뒤.
 */
export function RadioGroup<V extends string>({ options, value, onChange, className, ...rest }: RadioGroupProps<V>) {
  const plain = !options.some((o) => o.leading);
  return (
    <div role="radiogroup" className={cx(styles.root, plain && styles.plain, className)} {...rest}>
      {options.map((o) => {
        const selected = o.value === value;
        const radio = (
          <span className={styles.radio} aria-hidden>
            <span className={styles.dot} />
          </span>
        );
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={o.disabled}
            className={cx(styles.item, selected && styles.selected)}
            onClick={() => onChange(o.value)}
          >
            {plain && radio}
            {o.leading && <span className={styles.leading}>{o.leading}</span>}
            <span className={styles.texts}>
              <span className={styles.label}>{o.label}</span>
              {o.description && <span className={styles.description}>{o.description}</span>}
            </span>
            {!plain && radio}
          </button>
        );
      })}
    </div>
  );
}
